import {default as classnames} from "classnames";
import {FiDownload} from "react-icons/fi";
import {Link} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Table} from "@/components/table/Table.tsx";
import {Sorting} from "@/components/table/Table.types.ts";
import {dateOnly, shortCurrency} from "@/lib/format.tsx";
import {Invoice} from "@/lib/types/billing.ts";

export interface InvoicesTableProps {
  items: Invoice[];
  sorting: Sorting;
  onSetSorting: (sorting: Sorting) => void;
  pagination?: LimitAndOffset;
  totalRows?: number;
  updatePagination?: (laf: LimitAndOffset) => void;
}

export const InvoicesTable = ({
  items,
  sorting,
  onSetSorting,
  pagination,
  totalRows,
  updatePagination,
}: InvoicesTableProps) => {
  const {user} = useCurrentUserContext();

  const specs = [
    {
      fieldName: "transaction_id",
      fieldTitle: "Ref #",
      meta: {headerClassName: "text-uppercase p-2 w-15"},
      accessor: (obj: Invoice) => obj.transaction_id,
    },
    {
      fieldName: "created_at",
      fieldTitle: "Invoice Date",
      enableSorting: true,
      meta: {headerClassName: "text-uppercase p-2 w-15"},
      accessor: (obj: Invoice) => dateOnly(obj.created_at, user!.current_date_format, user!.account!.timezone),
    },
    {
      fieldName: "processor_response_text",
      fieldTitle: "Status",
      enableSorting: true,
      meta: {headerClassName: "text-uppercase p-2"},
      accessor: (obj: Invoice) =>
        obj.is_successful && obj.amount > 0 ? "Paid" : `Unpaid (${obj.processor_response_text})`,
    },
    {
      fieldName: "amount",
      fieldTitle: "Amount",
      enableSorting: true,
      meta: {headerClassName: "text-uppercase p-2 w-15"},
      accessor: (obj: Invoice) => (
        <span
          className={classnames({
            "text-danger text-decoration-line-through": !obj.is_successful,
            "text-warning": obj.is_successful && obj.amount < 0,
          })}
        >
          {shortCurrency(obj.amount, 2)}
        </span>
      ),
    },
    {
      fieldName: "legacy_plan",
      fieldTitle: "Subscription",
      meta: {headerClassName: "text-uppercase p-2 w-15"},
      accessor: (obj: Invoice) => obj.legacy_plan || "Flexible",
    },
    {
      fieldName: "is_successful",
      fieldTitle: "",
      meta: {headerClassName: "text-uppercase p-2 w-5", columnClassName: "text-center"},
      accessor: (obj: Invoice) =>
        obj.is_successful && obj.amount > 0 ? (
          <Link to={`/billing/history/${obj.transaction_id}`} target="_blank" className="btn bt-link">
            <FiDownload />
          </Link>
        ) : (
          false
        ),
    },
  ];
  return (
    <Table
      rows={items}
      minWidth="800px"
      specs={specs}
      sorting={sorting}
      onSortingChange={onSetSorting}
      pagination={pagination}
      totalRows={totalRows}
      updatePagination={updatePagination}
    />
  );
};
